import axios from 'axios';
import { APP_VERSION, HOST_API } from '../config';
import { getDeviceType, getUserLocation } from './getUserDetails';
import moment from 'moment';
import { dispatch } from 'src/redux/store';
import { submitReportError } from 'src/redux/pwa-report/submitError';

const axiosInstance = axios.create({
  baseURL: HOST_API,
});

const generateRandomId = (length) => {
  const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
};

axiosInstance.interceptors.request.use((config) => {
  const uniqueTimeStamp = `${new Date().getTime()}${generateRandomId(6)}`;
  config.headers['UUID'] = uniqueTimeStamp;
  config.headers['App-Version'] = APP_VERSION;
  config.metadata = { startTime: moment().format('YYYY-MM-DD HH:mm:ss') };

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // First log the error immediately
    console.error('Axios error:', error);

    // Start processing the error
    const processError = async () => {
      const end = moment().format('YYYY-MM-DD HH:mm:ss');
      const start = error.config?.metadata?.startTime || moment().format('YYYY-MM-DD HH:mm:ss');

      const startTime = moment(start, 'YYYY-MM-DD HH:mm:ss');
      const endTime = moment(end, 'YYYY-MM-DD HH:mm:ss');
      const duration = endTime.diff(startTime);

      // Get client info (this is the async part)
      const clientInfo = {
        device: getDeviceType(),
        location: await getUserLocation().catch(() => 'location_error') // Add fallback
      };

      let payload = '';

      if (typeof error?.config?.data === 'object') {
        if (error?.config?.data instanceof FormData) {
          payload = JSON.stringify(formDataToJson(error?.config?.data));
        } else {
          payload = JSON.stringify(error?.config?.data);
        }
      } else {
        payload = error?.config?.data;
      }

      function formDataToJson(formData) {
        const jsonObject = {};
        formData.forEach((value, key) => {
          if (jsonObject[key]) {
            jsonObject[key] = [].concat(jsonObject[key], value);
          } else {
            jsonObject[key] = value;
          }
        });
        return jsonObject;
      }

      const formData = new FormData();
      formData.append('source', "frontend");
      formData.append('method', error.config.method?.toUpperCase());
      formData.append('payload', payload ?? "");
      formData.append('response_code', error?.response?.status || 503);
      formData.append('response_body', error?.response ? JSON.stringify(error?.response?.data) : "Service Unavailable - No response from server");
      formData.append('error_type', error?.response?.statusText || "Service Unavailable");
      formData.append('error_occurred_at', moment().format('YYYY-MM-DD HH:mm:ss'));
      formData.append('url', `${HOST_API.slice(0, -1)}${error.config?.url}`);
      formData.append('app_version', APP_VERSION);
      formData.append('client_info', JSON.stringify(clientInfo));
      formData.append('start_time', start);
      formData.append('end_time', end);
      formData.append('duration_ms', error.response ? duration : moment(end).diff(moment(start)));
      formData.append('uuid', error.config.headers?.UUID);
      formData.append('status', "error");
      formData.append('headers', JSON.stringify(error?.config?.headers || {}));

      dispatch(submitReportError(formData, error.config.headers?.UUID));
    };

    // Start the async processing but don't wait for it
    processError().catch(e => console.error('Error processing error report:', e));

    // Immediately reject with the error
    return Promise.reject((error?.response && error?.response?.data) || 'Something went wrong');
  }
);

export default axiosInstance;
